import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  Button,
  Link
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Share as ShareIcon,
  Cookie as CookieIcon,
  Shield as ShieldIcon,
  Storage as StorageIcon,
  Gavel as GavelIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  Update as UpdateIcon,
  Help as HelpIcon,
  VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';

function PrivacyPolicy() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Smooth scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sections = [
    {
      id: 'intro',
      title: 'Introduction',
      icon: <VerifiedUserIcon />,
      color: '#1976d2'
    },
    {
      id: 'collect',
      title: 'Information We Collect',
      icon: <DataUsageIcon />,
      color: '#388e3c'
    },
    {
      id: 'use',
      title: 'How We Use Your Information',
      icon: <SecurityIcon />,
      color: '#f57c00'
    },
    {
      id: 'share',
      title: 'How We Share Your Information',
      icon: <ShareIcon />,
      color: '#7b1fa2'
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      icon: <CookieIcon />,
      color: '#d32f2f'
    },
    {
      id: 'security',
      title: 'Data Security',
      icon: <ShieldIcon />,
      color: '#0288d1'
    },
    {
      id: 'retention',
      title: 'Data Retention',
      icon: <StorageIcon />,
      color: '#e64a19'
    },
    {
      id: 'rights',
      title: 'Your Privacy Rights',
      icon: <GavelIcon />,
      color: '#00796b'
    },
    {
      id: 'marketing',
      title: 'Marketing Communications',
      icon: <EmailIcon />,
      color: '#5e35b1'
    },
    {
      id: 'third-party',
      title: 'Third-Party Services',
      icon: <LinkIcon />,
      color: '#c2185b'
    },
    {
      id: 'updates',
      title: 'Policy Updates',
      icon: <UpdateIcon />,
      color: '#795548'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4, pt: 12 }}>
      {/* Header */}
      <Box sx={{ mb: 8, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold', 
            color: '#1976d2',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 3
          }}
        >
          Circledin Privacy Policy
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
          Effective Date: January 1, 2025 | Last Updated: January 1, 2025 | Version: 3.0
        </Typography>
        <Chip 
          label="Current Version" 
          color="primary" 
          sx={{ 
            mt: 1,
            fontSize: '0.875rem',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)'
          }} 
        />
      </Box>

      {/* Quick Overview */}
      <Paper 
        elevation={6} 
        sx={{ 
          p: 5, 
          mb: 6, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}
      >
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Your Privacy Matters
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mb: 4, opacity: 0.9 }}>
          We are committed to protecting your personal information and being transparent about our data practices.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Data Security
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Industry-standard encryption and security measures
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Your Control
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Full control over your data and privacy settings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 2 }}>
                  Transparency
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.6 }}>
                  Clear information about data collection and use
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Important Notice */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 5, 
          p: 3,
          borderRadius: '12px',
          border: '1px solid #2196f3',
          boxShadow: '0 4px 16px rgba(33, 150, 243, 0.1)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          Important: This Privacy Policy should be read together with our Terms of Service.
        </Typography>
        <Typography variant="body1">
          By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.
        </Typography>
      </Alert>

      {/* Table of Contents */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          mb: 5,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            fontWeight: 'bold',
            color: '#1976d2',
            mb: 3
          }}
        >
          <HelpIcon sx={{ fontSize: '2rem' }} /> Table of Contents
        </Typography>
        <Grid container spacing={3}>
          {sections.map((section, index) => (
            <Grid item xs={12} sm={6} md={4} key={section.id}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={section.icon}
                onClick={() => document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })}
                sx={{ 
                  justifyContent: 'flex-start', 
                  color: section.color, 
                  borderColor: section.color,
                  borderRadius: '12px',
                  p: 2,
                  height: 'auto',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: `${section.color}10`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 16px ${section.color}30`
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                    {index + 1}. {section.title}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Privacy Policy Content */}
      <Box sx={{ mb: 6 }}>
        
        {/* Section 1: Introduction */}
        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleChange('panel1')} 
          id="intro"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <VerifiedUserIcon sx={{ mr: 2, color: '#1976d2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>1. Introduction</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography paragraph>
              Welcome to Circledin! We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how Circledin, LLC. ("Circledin," "we," "us," or "our") collects, uses, discloses, and protects your information when you use our website, mobile application, and related services (collectively, the "Service").
            </Typography>
            <Typography paragraph>
              This Privacy Policy should be read together with our Terms of Service. By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Section 2: Information We Collect */}
        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleChange('panel2')} 
          id="collect"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <DataUsageIcon sx={{ mr: 2, color: '#388e3c' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>2. Information We Collect</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Information You Provide Directly</Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Account Information:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Full name (first and last name)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Email address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Phone number" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Mailing address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Date of birth" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Profile photo (optional)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Username and password" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Mobile Account Information:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Current carrier account details" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Account number and PIN" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Phone numbers to be ported" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Service preferences and plan details" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Payment Information:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Credit card or bank account details" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Billing address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Payment history and transaction records" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Information We Collect Automatically</Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Usage Information:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="IP address and device identifiers" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Browser type and version" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Pages visited and features used" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Time and date of visits" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Device Information:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Device type (mobile, tablet, desktop)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Operating system" />
              </ListItem>
              <ListItem>
                <ListItemText primary="App version and usage statistics" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 3: How We Use Your Information */}
        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleChange('panel3')} 
          id="use"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <SecurityIcon sx={{ mr: 2, color: '#f57c00' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>3. How We Use Your Information</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Service Provision</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Create and manage user accounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Facilitate connections between Circle Owners and Joiners" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Process payments and manage subscriptions" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide customer support" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Verify identity and prevent fraud" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Service Improvement</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Analyze usage patterns and user behavior" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Develop new features and functionality" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Improve user experience and interface" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Conduct research and analytics" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Communications</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Send service-related notifications" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide customer support responses" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Send marketing communications (with consent)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Facilitate communication between users" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 4: How We Share Your Information */}
        <Accordion 
          expanded={expanded === 'panel4'} 
          onChange={handleChange('panel4')} 
          id="share"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <ShareIcon sx={{ mr: 2, color: '#7b1fa2' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>4. How We Share Your Information</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>With Other Users</Typography>
            <Typography paragraph>
              <strong>Circle Owners and Joiners:</strong> When you join a circle or accept someone into your circle, we share necessary information including:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Contact information (name, phone, email)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Account details needed for carrier transfers" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Billing and payment information as required" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>With Service Providers</Typography>
            <Typography paragraph>We share information with trusted service providers including:</Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Payment Processing" 
                  secondary="Stripe for payment processing and fraud prevention"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Identity Verification" 
                  secondary="Background check and identity verification services"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Communication Services" 
                  secondary="Email, SMS, and customer support platforms"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Technical Services" 
                  secondary="Cloud hosting, analytics, and security services"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>With Mobile Carriers</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Account transfer and porting information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Service activation details" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Billing and payment coordination" />
              </ListItem>
            </List>

            <Alert severity="warning" sx={{ mt: 3, borderRadius: '8px' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Legal Requirements:
              </Typography>
              <Typography variant="body2">
                We may disclose your information when required by law, to comply with legal process, protect against fraud, or ensure the safety of our users and the public.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Section 5: Cookies and Tracking */}
        <Accordion 
          expanded={expanded === 'panel5'} 
          onChange={handleChange('panel5')} 
          id="cookies"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <CookieIcon sx={{ mr: 2, color: '#d32f2f' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>5. Cookies and Tracking Technologies</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Types of Cookies We Use</Typography>
            
            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Essential Cookies:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Authentication and security" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Session management" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User preferences" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Analytics Cookies:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Google Analytics" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Usage tracking" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Performance monitoring" />
              </ListItem>
            </List>

            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>Marketing Cookies:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Advertising personalization" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Campaign tracking" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Social media integration" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Your Cookie Choices</Typography>
            <Typography paragraph>
              You can control cookies through your browser settings. However, disabling certain cookies may limit Service functionality.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Third-Party Tracking Opt-Out</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Google Analytics Opt-out Browser Add-on" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Network Advertising Initiative opt-out" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Digital Advertising Alliance opt-out" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 6: Data Security */}
        <Accordion 
          expanded={expanded === 'panel6'} 
          onChange={handleChange('panel6')} 
          id="security"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <ShieldIcon sx={{ mr: 2, color: '#0288d1' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>6. Data Security</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Security Measures</Typography>
            <Typography paragraph>
              We implement industry-standard security measures to protect your information:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="TLS/SSL encryption for data transmission" />
              </ListItem>
              <ListItem>
                <ListItemText primary="AES-256 encryption for data storage" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Multi-factor authentication" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Regular security audits and penetration testing" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Employee access controls and training" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Payment Security</Typography>
            <List>
              <ListItem>
                <ListItemText primary="PCI DSS Level 1 compliance through Stripe" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Tokenization of payment information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Fraud detection and monitoring" />
              </ListItem>
            </List>

            <Alert severity="info" sx={{ mt: 3, borderRadius: '8px' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Data Breach Response:
              </Typography>
              <Typography variant="body2">
                In the event of a data breach, we will investigate and contain the breach, report to appropriate authorities as required, and notify affected users with guidance on protective measures.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Section 7: Data Retention */}
        <Accordion 
          expanded={expanded === 'panel7'} 
          onChange={handleChange('panel7')} 
          id="retention"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <StorageIcon sx={{ mr: 2, color: '#e64a19' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>7. Data Retention</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Retention Periods</Typography>
            <Typography paragraph>
              We retain your information for different periods based on its type and purpose:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Account Information" 
                  secondary="Until account deletion plus 7 years for legal compliance"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Payment Information" 
                  secondary="7 years for financial and tax requirements"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Communication Records" 
                  secondary="3 years for customer support purposes"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Usage Data" 
                  secondary="2 years for analytics and service improvement"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Marketing Data" 
                  secondary="Until you opt out plus 30 days for processing"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>Deletion Process</Typography>
            <Typography paragraph>When you delete your account:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Active data is removed within 30 days" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Backup systems are cleared within 90 days" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Some information may be retained for legal compliance" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Anonymized data may be retained for analytics" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 8: Your Privacy Rights */}
        <Accordion 
          expanded={expanded === 'panel8'} 
          onChange={handleChange('panel8')} 
          id="rights"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <GavelIcon sx={{ mr: 2, color: '#00796b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>8. Your Privacy Rights</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>California Residents (CCPA/CPRA)</Typography>
            <Typography paragraph>
              California residents have additional rights including:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Right to know what information is collected" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to delete personal information" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to opt out of sale/sharing" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to non-discrimination" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Right to correct inaccurate information" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Other State Laws</Typography>
            <Typography paragraph>
              We comply with privacy laws in all applicable states, including Virginia, Colorado, Connecticut, and others as they take effect.
            </Typography>

            <Alert severity="success" sx={{ mt: 3, borderRadius: '8px' }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                Exercise Your Rights:
              </Typography>
              <Typography variant="body2">
                To exercise any of these rights, please contact us at privacy@circledin.com or through your account settings.
              </Typography>
            </Alert>
          </AccordionDetails>
        </Accordion>

        {/* Section 9: Marketing Communications */}
        <Accordion 
          expanded={expanded === 'panel9'} 
          onChange={handleChange('panel9')} 
          id="marketing"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <EmailIcon sx={{ mr: 2, color: '#5e35b1' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>9. Marketing and Communications</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Marketing Communications</Typography>
            <Typography paragraph>We may send you:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Product updates and announcements" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Promotional offers and discounts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Educational content about mobile plans" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Survey requests and feedback opportunities" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Opt-Out Options</Typography>
            <Typography paragraph>You can opt out of marketing communications by:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Clicking unsubscribe links in emails" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Updating preferences in your account" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Contacting customer support" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Responding 'STOP' to text messages" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Transactional Communications</Typography>
            <Typography paragraph>
              You cannot opt out of essential service communications such as:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Account security alerts" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Billing notifications" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Service updates" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Legal notices" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Section 10: Third-Party Services */}
        <Accordion 
          expanded={expanded === 'panel10'} 
          onChange={handleChange('panel10')} 
          id="third-party"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <LinkIcon sx={{ mr: 2, color: '#c2185b' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>10. Third-Party Services</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Third-Party Links</Typography>
            <Typography paragraph>
              Our Service may contain links to third-party websites and services. We are not responsible for their privacy practices. We encourage you to review their privacy policies before providing any information.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Social Media Integration</Typography>
            <Typography paragraph>
              When you connect social media accounts, information may be shared according to your social media privacy settings. Please review the privacy policies of social media platforms you choose to connect.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Carrier Integration</Typography>
            <Typography paragraph>
              We share necessary information with mobile carriers to provide our services, subject to their privacy policies. Each carrier has their own privacy practices that govern how they handle your information.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Section 11: Policy Updates */}
        <Accordion 
          expanded={expanded === 'panel11'} 
          onChange={handleChange('panel11')} 
          id="updates"
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            '&:before': { display: 'none' },
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            '&.Mui-expanded': {
              margin: '0 0 16px 0'
            }
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fc',
              borderRadius: '12px',
              '&.Mui-expanded': {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            }}
          >
            <UpdateIcon sx={{ mr: 2, color: '#795548' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>11. Updates to This Privacy Policy</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 4, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>Policy Changes</Typography>
            <Typography paragraph>We may update this Privacy Policy to reflect:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Changes in our practices" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Legal or regulatory requirements" />
              </ListItem>
              <ListItem>
                <ListItemText primary="New features or services" />
              </ListItem>
              <ListItem>
                <ListItemText primary="User feedback and concerns" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Notice of Changes</Typography>
            <Typography paragraph>We will notify you of material changes by:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Email notification to your registered address" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Prominent notice on our website" />
              </ListItem>
              <ListItem>
                <ListItemText primary="In-app notifications" />
              </ListItem>
              <ListItem>
                <ListItemText primary="30 days advance notice for significant changes" />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Continued Use</Typography>
            <Typography paragraph>
              Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Footer */}
      <Box sx={{ 
        mt: 8, 
        textAlign: 'center', 
        borderTop: '2px solid #e3f2fd', 
        pt: 4,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        borderRadius: '12px',
        p: 4
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2 }}>
          Questions about our Privacy Policy?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Contact us at <Link href="mailto:privacy@circledin.com">support@circledin.com</Link>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Last Updated: January 1, 2025 | Version: 3.0
        </Typography>
        <Typography variant="body2" color="text.secondary">
          © 2025 Circledin, LLC. All rights reserved.
        </Typography>
      </Box>
    </Container>
  );
}

export default PrivacyPolicy;